import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Table,
  Badge,
  Spinner,
  Alert,
  Card,
  ToggleButtonGroup,
  ToggleButton
} from 'react-bootstrap';
import {
  FiMail,
  FiMessageSquare,
  FiSend,
  FiEye,
  FiCheck,
  FiX,
  FiAlertTriangle
} from 'react-icons/fi';
import {
  TowingEligibleClient,
  TowingNotificationPreview,
  SendNotificationsResponse,
  NotificationType
} from '../../Types';
import {
  previewTowingNotifications,
  sendTowingNotifications
} from '../../requests/towingNotification.request';

type Props = {
  show: boolean;
  onHide: () => void;
  onNotificationsSent?: () => void;
  clients: TowingEligibleClient[];
};

type ViewState = 'select' | 'preview' | 'results';

const TowingCommunicationModal: React.FC<Props> = ({
  show,
  onHide,
  onNotificationsSent,
  clients
}) => {
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [notificationType, setNotificationType] = useState<NotificationType>(NotificationType.Sms);
  const [previews, setPreviews] = useState<TowingNotificationPreview[]>([]);
  const [results, setResults] = useState<SendNotificationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewState, setViewState] = useState<ViewState>('select');
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSelectedClientIds(new Set());
      setViewState('select');
      setPreviews([]);
      setResults(null);
      setSelectedPreviewIndex(0);
      setError(null);
    }
  }, [show]);

  const handleSelectAll = () => {
    if (selectedClientIds.size === selectableClients.length) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(selectableClients.map((c) => c.clientId)));
    }
  };

  const handleSelectClient = (clientId: string) => {
    const newSelected = new Set(selectedClientIds);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClientIds(newSelected);
  };

  const handlePreview = async () => {
    if (selectedClientIds.size === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await previewTowingNotifications({
        clientIds: Array.from(selectedClientIds),
        notificationType: notificationType
      });
      if (response?.success && response.data) {
        setPreviews(response.data);
        setViewState('preview');
        setSelectedPreviewIndex(0);
      } else {
        setError(response?.errors?.[0] || 'Failed to generate previews');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate previews');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (selectedClientIds.size === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await sendTowingNotifications({
        clientIds: Array.from(selectedClientIds),
        notificationType: notificationType
      });
      if (response?.success && response.data) {
        setResults(response.data);
        setViewState('results');
        onNotificationsSent?.();
      } else {
        setError(response?.errors?.[0] || 'Failed to send notifications');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (viewState === 'preview') {
      setViewState('select');
      setPreviews([]);
    } else if (viewState === 'results') {
      setViewState('select');
      setResults(null);
      setSelectedClientIds(new Set());
    }
  };

  const handleClose = () => {
    setViewState('select');
    setPreviews([]);
    setResults(null);
    setSelectedClientIds(new Set());
    onHide();
  };

  const getClientContactInfo = (client: TowingEligibleClient) => {
    if (notificationType === NotificationType.Email) {
      return client.emailAddress || 'No email';
    }
    return client.phoneNumber || 'No phone';
  };

  const isClientSelectable = (client: TowingEligibleClient) => {
    if (notificationType === NotificationType.Email) {
      return !!client.emailAddress;
    }
    return !!client.phoneNumber;
  };

  const selectableClients = clients.filter(isClientSelectable);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton className="bg-danger text-white border-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <FiAlertTriangle size={24} />
          <span>Towing Warning Notifications</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {viewState === 'select' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <span className="fw-bold">Notification Type:</span>
                <ToggleButtonGroup
                  type="radio"
                  name="notificationType"
                  value={notificationType}
                  onChange={(val) => {
                    setNotificationType(val);
                    setSelectedClientIds(new Set());
                  }}>
                  <ToggleButton
                    id="toggle-sms-towing"
                    value={NotificationType.Sms}
                    variant={
                      notificationType === NotificationType.Sms ? 'success' : 'outline-success'
                    }
                    className="d-flex align-items-center gap-2">
                    <FiMessageSquare /> SMS
                  </ToggleButton>
                  <ToggleButton
                    id="toggle-email-towing"
                    value={NotificationType.Email}
                    variant={
                      notificationType === NotificationType.Email ? 'primary' : 'outline-primary'
                    }
                    className="d-flex align-items-center gap-2">
                    <FiMail /> Email
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Badge bg="secondary" className="py-2 px-3">
                  {selectedClientIds.size} of {selectableClients.length} selected
                </Badge>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={selectableClients.length === 0}>
                  {selectedClientIds.size === selectableClients.length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>
            </div>

            {clients.length === 0 ? (
              <Alert variant="success">No clients currently at risk of towing.</Alert>
            ) : (
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table hover className="align-middle mb-0 bg-white shadow-sm">
                  <thead className="bg-danger text-white" style={{ position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Client</th>
                      <th>{notificationType === NotificationType.Email ? 'Email' : 'Phone'}</th>
                      <th>Interest Owed</th>
                      <th>Plates</th>
                      <th>Last Notified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => {
                      const selectable = isClientSelectable(client);
                      return (
                        <tr
                          key={client.clientId}
                          className={!selectable ? 'table-secondary' : ''}
                          style={{ cursor: selectable ? 'pointer' : 'not-allowed' }}
                          onClick={() => selectable && handleSelectClient(client.clientId)}>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={selectedClientIds.has(client.clientId)}
                              onChange={() => {}}
                              disabled={!selectable}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{client.clientName}</div>
                          </td>
                          <td>
                            <span className={!selectable ? 'text-danger' : 'text-muted'}>
                              {getClientContactInfo(client)}
                            </span>
                          </td>
                          <td>
                            <Badge bg="danger" className="px-3 py-2">
                              ${client.totalInterestOwed.toFixed(2)}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {client.plateNumbers.map((plate) => (
                                <Badge key={plate} bg="warning" text="dark" className="fw-normal">
                                  {plate}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            <span className="text-muted small">
                              {formatDate(client.lastTowingNotificationSentAt)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {viewState === 'preview' && previews.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0">
                <FiEye className="me-2" />
                Message Preview ({selectedPreviewIndex + 1} of {previews.length})
              </h5>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={selectedPreviewIndex === 0}
                  onClick={() => setSelectedPreviewIndex((i) => i - 1)}>
                  Previous
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={selectedPreviewIndex === previews.length - 1}
                  onClick={() => setSelectedPreviewIndex((i) => i + 1)}>
                  Next
                </Button>
              </div>
            </div>

            <Card className="shadow-sm border-danger">
              <Card.Header className="bg-white border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>To:</strong> {previews[selectedPreviewIndex].clientName}
                  </div>
                  <div className="d-flex gap-2">
                    <Badge bg="danger">
                      ${previews[selectedPreviewIndex].totalInterestOwed.toFixed(2)} owed
                    </Badge>
                    {previews[selectedPreviewIndex].notificationType === NotificationType.Sms &&
                      previews[selectedPreviewIndex].sendCount > 0 && (
                        <Badge bg="warning" text="dark">
                          Reminder #{previews[selectedPreviewIndex].sendCount}
                        </Badge>
                      )}
                    <Badge
                      bg={
                        previews[selectedPreviewIndex].notificationType === NotificationType.Email
                          ? 'primary'
                          : 'success'
                      }>
                      {previews[selectedPreviewIndex].notificationType === NotificationType.Email
                        ? 'Email'
                        : 'SMS'}
                    </Badge>
                  </div>
                </div>
                <div className="text-muted small mt-1">
                  <strong>Recipient:</strong> {previews[selectedPreviewIndex].recipient}
                </div>
                {previews[selectedPreviewIndex].subject && (
                  <div className="text-muted small">
                    <strong>Subject:</strong> {previews[selectedPreviewIndex].subject}
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <pre
                  className="mb-0"
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    backgroundColor: '#fff5f5',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #f8d7da'
                  }}>
                  {previews[selectedPreviewIndex].message}
                </pre>
              </Card.Body>
            </Card>

            <div className="mt-4">
              <h6>All Recipients ({previews.length})</h6>
              <div className="d-flex flex-wrap gap-2">
                {previews.map((preview, index) => (
                  <Badge
                    key={preview.clientId}
                    bg={index === selectedPreviewIndex ? 'danger' : 'secondary'}
                    className="py-2 px-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedPreviewIndex(index)}>
                    {preview.clientName}
                    <span className="ms-1 badge bg-dark" style={{ fontSize: '0.7em' }}>
                      ${preview.totalInterestOwed.toFixed(0)}
                    </span>
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {viewState === 'results' && results && (
          <>
            <div className="text-center mb-4">
              <div className="d-flex justify-content-center gap-4 mb-4">
                <div className="text-center">
                  <div
                    className="rounded-circle bg-success d-flex align-items-center justify-content-center mx-auto mb-2"
                    style={{ width: '80px', height: '80px' }}>
                    <FiCheck size={40} color="white" />
                  </div>
                  <h3 className="mb-0">{results.totalSent}</h3>
                  <small className="text-muted">Sent Successfully</small>
                </div>
                {results.totalFailed > 0 && (
                  <div className="text-center">
                    <div
                      className="rounded-circle bg-danger d-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{ width: '80px', height: '80px' }}>
                      <FiX size={40} color="white" />
                    </div>
                    <h3 className="mb-0">{results.totalFailed}</h3>
                    <small className="text-muted">Failed</small>
                  </div>
                )}
              </div>
            </div>

            <Table hover className="bg-white shadow-sm">
              <thead className="bg-danger text-white">
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((result) => (
                  <tr key={result.clientId}>
                    <td className="fw-semibold">{result.clientName}</td>
                    <td>
                      {result.success ? (
                        <Badge
                          bg="success"
                          className="d-flex align-items-center gap-1"
                          style={{ width: 'fit-content' }}>
                          <FiCheck /> Sent
                        </Badge>
                      ) : (
                        <Badge
                          bg="danger"
                          className="d-flex align-items-center gap-1"
                          style={{ width: 'fit-content' }}>
                          <FiX /> Failed
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted">
                      {result.errorMessage || 'Notification sent successfully'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light border-top">
        {viewState === 'select' && (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handlePreview}
              disabled={selectedClientIds.size === 0 || loading}
              className="d-flex align-items-center gap-2">
              <FiEye /> Preview Messages
            </Button>
          </>
        )}

        {viewState === 'preview' && (
          <>
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="danger"
              onClick={handleSend}
              disabled={loading}
              className="d-flex align-items-center gap-2">
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend /> Send {notificationType === NotificationType.Email ? 'Emails' : 'SMS'} (
                  {previews.length})
                </>
              )}
            </Button>
          </>
        )}

        {viewState === 'results' && (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TowingCommunicationModal;
